import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Send, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import BottomNav from '../components/BottomNav';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { getPrimaryPhoto } from '../services/photoHelpers';
import { getIcebreakerRecommendations, getIcebreakers, sendIcebreaker } from '../services/api';

const ConnectPage = () => {
  const navigate = useNavigate();
  const defaultAvatar = useDefaultAvatar();
  const [recs, setRecs] = useState([]);
  const [selected, setSelected] = useState({}); // { user_id: true }
  const [suggestions, setSuggestions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      getIcebreakerRecommendations(12).then(r => setRecs(r.data || [])).catch(() => {}),
      getIcebreakers().then(r => setSuggestions(r.data.messages || [])).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const toggleUser = (uid) => setSelected(prev => ({ ...prev, [uid]: !prev[uid] }));
  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const handleSend = async () => {
    setError(''); setResult(null);
    if (!message.trim()) { setError('Write a message first'); return; }
    if (selectedIds.length === 0) { setError('Pick at least one person'); return; }
    setSending(true);
    try {
      const res = await sendIcebreaker(selectedIds, message.trim());
      setResult(res.data);
      setSelected({});
      setMessage('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white pb-40" data-testid="connect-page">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-lg border-b border-yellow-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-yellow-50" data-testid="connect-back-btn">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Let's Connect</h1>
            <p className="text-xs text-gray-500">Send an icebreaker to multiple matches at once</p>
          </div>
          <Sparkles className="w-5 h-5 text-yellow-500" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Recommendations */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Recommended for you ({selectedIds.length} selected)</h3>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-yellow-500 animate-spin" /></div>
          ) : recs.length === 0 ? (
            <div className="text-sm text-gray-500 py-6">No recommendations yet — complete your profile to get better matches.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {recs.map(r => {
                const u = r.user;
                const photo = getPrimaryPhoto(u, defaultAvatar) || defaultAvatar;
                const isSel = !!selected[u.user_id];
                return (
                  <button
                    key={u.user_id}
                    onClick={() => toggleUser(u.user_id)}
                    className={`relative rounded-2xl overflow-hidden aspect-[3/4] border-4 transition-colors ${isSel ? 'border-[#009E60]' : 'border-transparent'}`}
                    data-testid={`connect-user-${u.user_id}`}
                  >
                    <img src={photo} alt={u.display_name || u.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-1 left-2 right-2 text-white text-[11px] font-semibold text-left truncate">
                      {u.display_name || u.name}{u.age ? `, ${u.age}` : ''}
                    </div>
                    {r.match_score >= 60 && (
                      <span className="absolute top-1.5 left-1.5 bg-[#E2231A] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        {r.match_score}%
                      </span>
                    )}
                    {isSel && (
                      <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#009E60] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Suggested messages */}
        {suggestions.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Ideas</h3>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setMessage(s)}
                  className="flex-shrink-0 max-w-[260px] text-left bg-white border border-yellow-200 rounded-2xl px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50"
                  data-testid={`icebreaker-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </section>
        )}

        {result && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-2xl p-3 text-sm" data-testid="connect-result">
            Sent to {result.sent?.length || 0} · Skipped {result.skipped?.length || 0}
          </div>
        )}
        {error && (
          <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm" data-testid="connect-error">{error}</div>
        )}
      </div>

      {/* Composer */}
      <div className="fixed bottom-16 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200 safe-bottom z-20">
        <div className="max-w-2xl mx-auto p-3 flex items-end gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 280))}
            placeholder="Your icebreaker message…"
            className="min-h-[52px] max-h-24 rounded-2xl resize-none"
            data-testid="connect-message-input"
          />
          <Button
            onClick={handleSend}
            disabled={sending || !message.trim() || selectedIds.length === 0}
            className="h-12 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4"
            data-testid="connect-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ConnectPage;
